/**
 * تنظیمات — قرینه‌ی apps/mobile/app/(tabs)/settings.tsx.
 *
 * دو چیزِ موبایل عمداً اینجا نیست: کارتِ «مخاطبین» (مرورگر دفترچه ندارد)
 * و داربستِ لاگِ روی دستگاه (`TRACE_OWNER`) — آن یکی مشکلِ خودِ گوشی را حل
 * می‌کند، چیزی که وب اصلاً ندارد.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/core/session';
import { contactKeyRepo } from '@/core/data/phoneRepo';
import { getLocale, t, useLocale } from '@/core/lib/i18n';
import { startTour } from '@/core/lib/tour';
import { legalDoc } from '@/core/legal/terms';
import { Avatar, Card, Note } from '@/ui/kit';
import { useTheme } from '@/ui/theme';

export function SettingsPage() {
  useLocale();
  const { profile, session, signOut } = useSession();
  const { isDark, toggle } = useTheme();
  const doc = legalDoc(getLocale());

  /**
   * کلیدی که دوستان با آن پیدایم می‌کنند.
   *
   * `session.user.email` می‌گوید با چه حسابی وارد شده‌ام؛ این می‌گوید
   * دیتابیس چه چیزی را برای **تطبیق** نگه داشته. معمولاً یکی‌اند، ولی
   * وقتی یکی نباشند دقیقاً همان‌جاست که «چرا دوستم پیدایم نکرد» جواب
   * می‌گیرد — پس هر دو نشان داده می‌شوند، نه یکی.
   */
  const emailKey = useQuery({
    queryKey: ['myEmailKey'],
    queryFn: contactKeyRepo.myEmail,
    enabled: !!session,
  });

  return (
    <div className="feed">
      <h1 className="feed-title">{t('settings')}</h1>

      <Link to="/profile" className="settings-link">
        <Card>
          <div className="identity-card">
            <Avatar url={profile?.avatarUrl} name={profile?.displayName} size={72} />
            <h2>{profile?.displayName ?? ''}</h2>
            {profile?.username ? <p className="dim">@{profile.username}</p> : null}
            <p className="dim">{t('change')}</p>
          </div>
        </Card>
      </Link>

      {/* ــ حسابی که باهاش واردی ــ
          تا امروز هیچ‌جای اپ نوشته نمی‌شد. برای کسی که با گوگل وارد شده،
          «کدوم ایمیل؟» اصلاً جوابی نداشت — نه توی پروفایل، نه این‌جا. */}
      {session ? (
        <Card>
          <h2>{t('accountEmail')}</h2>
          <p className="mono-line">{session.user.email ?? t('notSetYet')}</p>
          {emailKey.isLoading ? <p className="dim">…</p> : null}
          {!emailKey.isLoading && emailKey.data ? (
            <>
              <p className="dim">{t('foundByEmail')}</p>
              {/* اگه کلیدِ ثبت‌شده با ایمیلِ حساب فرق داشته باشه، همون فرق
                  جوابِ «چرا دوستم پیدام نکرد» است. */}
              {emailKey.data !== (session.user.email ?? '').toLowerCase()
                ? <p className="mono-line dim">{emailKey.data}</p> : null}
            </>
          ) : null}
          {!emailKey.isLoading && !emailKey.data
            ? <Note tone="warn">{t('foundByEmailMissing')}</Note> : null}
        </Card>
      ) : null}

      {/* شهر بلافاصله بعد از کارتِ هویت: از همه‌ی چیزهای این صفحه، این
          یکیه که هر بار باز کردنِ اپ رو عوض می‌کنه. */}
      <h3>{t('myProfile')}</h3>
      <Link to="/settings/city" className="settings-link">
        <Card>
          <h2>{t('defaultCity')}</h2>
          <p className="dim">{profile?.city || t('notSetYet')}</p>
        </Card>
      </Link>

      <Link to="/availability" className="settings-link">
        <Card>
          <h2>{t('availability')}</h2>
          <p className="dim">{t('change')}</p>
        </Card>
      </Link>

      <h3>{t('settings')}</h3>
      <Card>
        <label className="radio-row" style={{ borderBottom: 'none' }}>
          <input type="checkbox" checked={isDark} onChange={toggle} />
          <span><b>{t('themeToggle')}</b></span>
        </label>
      </Card>

      <Link to="/settings/notifications" className="settings-link">
        <Card><h2>{t('notifSettings')}</h2></Card>
      </Link>

      <Link to="/settings/followed-tags" className="settings-link">
        <Card>
          <h2>{t('followTags')}</h2>
          <p className="dim">{t('followTagsSub')}</p>
        </Card>
      </Link>

      {/* تنها جای اپ که می‌شود مسدودیتی را برداشت: خودِ آن نفر از همه‌جای
          دیگر ناپیداست، پس دکمه‌ای کنارِ اسمش نمانده که بشود زد. */}
      <Link to="/settings/blocked" className="settings-link">
        <Card>
          <h2>{t('blockedUsers')}</h2>
          <p className="dim">{t('blockedUsersSub')}</p>
        </Card>
      </Link>

      {/* «دوباره ببین» برای تورِ کاربرِ تازه (WHOSUP-83).
          ارزان است و «اشتباهی زدم رد شد» را جبران می‌کند. ناوبری لازم ندارد:
          کارتِ تور در `Shell` است، پس روی همین صفحه هم باز می‌شود. */}
      {session ? (
        <button type="button" className="settings-link" data-testid="tour-again"
          onClick={() => startTour(session.user.id)}>
          <Card>
            <h2>{t('tourAgain')}</h2>
            <p className="dim">{t('tourAgainHint')}</p>
          </Card>
        </button>
      ) : null}

      <Link to="/legal" className="settings-link">
        <Card>
          <h2>{doc.title}</h2>
          <p className="dim">{doc.updated}</p>
        </Card>
      </Link>

      <Link to="/feedback" className="settings-link">
        <Card><h2>{t('feedback')}</h2></Card>
      </Link>

      {/* ابزارِ بازبینی به اپِ ادمین رفت، پس این لینک بیرون از این اپ می‌رود
          و `Link` نیست: مقصدش دامنه‌ی دیگری است. */}
      {profile?.isAdmin ? (
        <a href="https://admin.outami.com" target="_blank" rel="noreferrer" className="settings-link">
          <Card>
            <h2>{t('adminPendingTitle')}</h2>
            <p className="dim">{t('adminToolsHint')}</p>
          </Card>
        </a>
      ) : null}

      <button type="button" className="btn ghost block" onClick={() => void signOut()}>
        {t('signOut')}
      </button>

      <Link to="/settings/delete-account" className="danger-link">{t('deleteAccount')}</Link>

      <p className="version-line">{t('appName')}</p>
    </div>
  );
}
